import React, { useState } from 'react';
import { Shield, MessageCircle, CheckCircle, X, TrendingUp, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import RegistrationModal from './RegistrationModal';

const ReadyToSecureCTA = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('registration');

  const openModal = (type) => {
    setModalType(type);
    setIsModalOpen(true);
  };

  return (
    <>
      <RegistrationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialType={modalType}
      />
    <div className="relative py-20 overflow-hidden">
      {/* Background with subtle pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#0f0f1e]">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, #FF035B 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>
      
      {/* Floating gradient orbs */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-[#FF035B]/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#2F1A8E]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative bg-gradient-to-br from-[#1a1a2e]/80 via-[#2a1a3e]/80 to-[#1a1a2e]/80 backdrop-blur-xl text-white rounded-3xl px-8 py-16 md:px-16 md:py-20 border border-[#FF035B]/20 shadow-2xl">
          
          {/* Decorative top line */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#FF035B] to-transparent"></div>
          
          {/* Main CTA Content */}
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">
              Stop Losing Money to
              <br />
              <span className="bg-gradient-to-r from-[#FF035B] via-[#FF1493] to-[#2F1A8E] bg-clip-text text-transparent">
                Unprotected Brands
              </span>
            </h2>
            
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-4">
              Every day without trademark protection puts your brand at risk. Get ironclad legal protection in just 24-48 hours for only $45.
            </p>
            
            <div className="flex items-center justify-center gap-2 text-gray-400">
              <Award className="w-5 h-5 text-[#FF035B]" />
              <span className="text-sm">98% success rate • 10,000+ brands protected</span>
            </div>
          </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
          <button
            onClick={() => openModal('registration')}
            className="bg-[#FF035B] hover:bg-[#2F1A8E] text-white font-bold px-8 py-3 rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center gap-2"
          >
            <Shield className="w-5 h-5" />
            Protect Your Brand Now →
          </button>
          
          <button
            onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }}
            className="border-2 border-white text-white hover:bg-white hover:text-[#2F1A8E] font-semibold px-8 py-3 rounded-lg transition-all duration-300 flex items-center gap-2"
          >
            <MessageCircle className="w-5 h-5" />
            Get Expert Advice
          </button>
        </div>

        {/* Trust Indicators - Enhanced Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="flex flex-col items-center gap-3 p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:border-[#FF035B]/40 transition-all duration-300">
            <div className="w-12 h-12 bg-gradient-to-br from-[#FF035B] to-[#2F1A8E] rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <span className="text-white font-semibold text-lg">Zero Risk Guarantee</span>
            <span className="text-gray-400 text-sm text-center">Money back if your mark isn't available</span>
          </div>
          
          <div className="flex flex-col items-center gap-3 p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:border-[#FF035B]/40 transition-all duration-300">
            <div className="w-12 h-12 bg-gradient-to-br from-[#FF035B] to-[#2F1A8E] rounded-full flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-white font-semibold text-lg">Transparent Pricing</span>
            <span className="text-gray-400 text-sm text-center">Just $45 + filing fees. No surprises</span>
          </div>
          
          <div className="flex flex-col items-center gap-3 p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:border-[#FF035B]/40 transition-all duration-300">
            <div className="w-12 h-12 bg-gradient-to-br from-[#FF035B] to-[#2F1A8E] rounded-full flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <span className="text-white font-semibold text-lg">Lightning Fast</span>
            <span className="text-gray-400 text-sm text-center">Filed within 24-48 hours guaranteed</span>
          </div>
        </div>

        {/* Decorative bottom line */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#2F1A8E] to-transparent"></div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ReadyToSecureCTA;
